import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { productOrderCancellationSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { FormHeader } from '../FormHeader'
import { cancelProductOrder } from '@state/reducers/productOrderReducer'
import { CancelProductOrderDto, ProductOrderCancellationReason, ProductOrderDto, ProductOrderStatus, SupplierContactType } from '@services/dtos'
import SelectField from '@components/inputs/SelectField'
import { ProductOrderCancellationReasonLabels } from '@utils/constants'
import InputField from '@components/inputs/InputField'
import ConfirmDialog from '@components/dialogs/ConfirmDialog'
import SelectSupplierEmail from '@components/SelectSupplierEmail'
import { truncate } from 'lodash'

const PRODUCT_ORDER_CANCELLATION_REASON_OPTIONS = (
  Object.entries(ProductOrderCancellationReasonLabels) as Array<
    [ProductOrderCancellationReason, string]
  >
).map(([value, label]) => ({ label, value }))

interface Props {
  productOrder: ProductOrderDto
  onClose: () => void
}

const ProductOrderCancellationForm: React.FC<Props> = ({ productOrder, onClose }) => {
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false)

  const defaultEmail = React.useMemo(() => {
    const salesAdminContact = productOrder.supplier.contacts.find(({ type, email }) => 
      type === SupplierContactType.SALES_ADMINISTRATION && email)
    if (salesAdminContact) {
      return salesAdminContact.id
    }
    const firstEmailContact = productOrder.supplier.contacts.find(({ email }) => email)
    return firstEmailContact?.id
  }, [productOrder.supplier.contacts])
  const {
    handleSubmit,
    reset,
    watch,
    control
  } = useForm<CancelProductOrderDto>({
    resolver: yupResolver(productOrderCancellationSchema), 
    context: { productOrder },
    defaultValues: { supplierContactId: defaultEmail }
  })

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CancelProductOrderDto) => {
    setIsSubmitting(true)
    dispatch(cancelProductOrder({ id: productOrder.id, dto: data }))
      .unwrap()
      .then(() => {
        cb('La commande a été annulée avec succès')
        onClose()
      })
      .catch(() => cb('Une erreur est survenue'))
      .finally(() => setIsSubmitting(false))
  }

  const handleValidateBeforeConfirm = handleSubmit(
    () => setConfirmDialogOpen(true),
    () => {}
  )

  return (
    <div className="flex w-full h-full p-4">
      <form
        onSubmit={handleValidateBeforeConfirm}
        className="w-full h-full flex flex-col"
      >
        <FormHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          title={'Annulation'}
          subTitles={[
            `Commande ${productOrder.internalOrderNumber}`,
            `${truncate(productOrder.supplier.name, { length: 25, omission: '…' })} 
            - ${new Date(productOrder.createdAt).toLocaleDateString('fr-FR')}`
          ]}
        />
        {productOrder.status === ProductOrderStatus.PENDING_ORDER_ACKNOWLEDGMENT && (
          <SelectSupplierEmail
            className="mt-2"
            supplier={productOrder.supplier}
            control={control}
          />
        )}
        <div className="mb-4">
          <SelectField
            control={control}
            name={'reason'}
            options={PRODUCT_ORDER_CANCELLATION_REASON_OPTIONS}
            value={watch('reason')}
            label={'Raison'}
          />
        </div>
        <InputField
          control={control}
          name={'comment'}
          label={`Commentaire ${productOrder.status === ProductOrderStatus.PENDING_VALIDATION ? 'interne' : 'au fournisseur'}`}
          multiline
          rows={6}
        />
      </form>

      <ConfirmDialog
        open={confirmDialogOpen}
        title="Annulation commande"
        message={`Êtes-vous sûr de vouloir annuler la commande ${productOrder.internalOrderNumber} ?
          ${productOrder.status === ProductOrderStatus.PENDING_ORDER_ACKNOWLEDGMENT ? ' Un email sera envoyé au fournisseur.' : ''}`}
        onCancel={() => setConfirmDialogOpen(false)}
        onConfirm={() => {
          setConfirmDialogOpen(false)
          handleSubmit(onSubmitForm)()
        }}
      />
    </div>
  )
}

export default ProductOrderCancellationForm
